import i18n from '@/i18n'
import {
  computed,
  onMounted,
  reactive,
  ref,
  toRefs,
  unref,
  watch
} from '@vue/composition-api'
import type { SetupContext } from '@vue/composition-api'
import type { forbidPageStatusType, forbidPagePropsType } from '../props'
import type { AppealStatus, UserInfo } from './interface'
import IMG_1 from '@/assets/forbid.svg'
import IMG_1_MOBILE from '@/assets/forbid-mobile.svg'
import IMG_2 from '@/assets/forbidSuccess.svg'
import { downloadFile, fetchFileInfo } from '@/utils/common'
import { getAppealStatus, getUserInfo } from '@/services/api'
import illegalAppealMobilePage from '@/components/illegalAppeal/src/appealPageMobile.vue'
import VueRouter from 'vue-router'
type useForbidPageOptType = {
  autoFetch: boolean; // 是否自动获取文件状态
  props: Partial<forbidPagePropsType>;
  listeners: Record<string, Function>;
  handleXhrError: Function;
};
type stateType = {
  tipsImg: string;
  text1: string;
  text2: string;
  text3: string;
  btn1: string;
  btn2: string;
};
const methodsKeys = ['onBtn1Click', 'onBtn2Click'] as const
type methodType = { [key in (typeof methodsKeys)[number]]: Function };
type forbidPageStateType = Record<forbidPageStatusType, stateType>;
type forbidPageMethodType = Partial<
  Record<forbidPageStatusType, Partial<methodType>>
>;
export const forbidPageRouterMobile = new VueRouter({
  mode: 'abstract',
  routes: [
    {
      path: '/',
      name: 'defaultPage'
    },
    {
      path: '/illegalAppeal',
      name: 'illegalAppeal',
      components: {
        illegalAppeal: illegalAppealMobilePage
      }
    }
  ]
})
const useForbidPage = ({
  autoFetch = true,
  props = {},
  listeners = {},
  handleXhrError = () => {}
}: Partial<useForbidPageOptType> = {}) => {
  const isForbidden = ref(false)
  const uiState = reactive({
    showNewTopBar: false,
    showTopBarDefault: true,
    // 是否展示通用错误页
    showErrorPage: false,
    // 是否展示 router-view
    showEntryRouter: false,
    // 如果不是禁用状态文件才展示 loading
    showLoading: false,
    // 是否展示禁用页
    showForbiddenPage: false
  })
  const useProps = ref<forbidPagePropsType>({
    // 根据传入参数决定传入页面的 props
    isMobile: false,
    status: 'shareForbid',
    ...props
  })
  const useListeners = {
    statusChange({
      status
    }: {
      status?: forbidPageStatusType;
    } = {}) {
      if (status) {
        useProps.value.status = status
      }
    },
    ...listeners
  }
  const flushUiState = () => {
    uiState.showNewTopBar = !isForbidden.value
    uiState.showTopBarDefault = !!isForbidden.value
    uiState.showErrorPage = !isForbidden.value
    uiState.showEntryRouter = !isForbidden.value
    uiState.showLoading = !isForbidden.value
    uiState.showForbiddenPage = !!isForbidden.value
  }
  const fetchStatus = async () => {
    let res!: AppealStatus
    let fileId = props.fileId
    const fetchUserPromise = getUserInfo()
    try {
      // 先获取用户信息，判断登录状态
      const currentUser: UserInfo = await fetchUserPromise
      // 获取封禁文件信息
      let { fileinfo } = await fetchFileInfo({
        fileId,
        sId: props.sId
      })
      // 通过接口判断文件是否为禁用
      res = await getAppealStatus(fileinfo.fileid)
      isForbidden.value = res.data.is_forbidden
      useProps.value.groupId = fileinfo.groupid
      useProps.value.fileId = fileinfo.fileid
      const checkForbidenStatus: () => forbidPageStatusType = () => {
        const isCreator = currentUser.userid + '' === fileinfo.creator.id + ''
        let status: forbidPageStatusType = 'shareForbid'
        const conditionHandlers: Array<{
          condition: (...args: Array<any>) => boolean;
          deal: (...args: Array<any>) => forbidPageStatusType;
          breakReturn?: boolean;
          [key: string]: any;
        }> = [
          {
            condition: () => !isCreator && res.data.is_forbidden,
            deal: () => 'shareForbid'
          },
          {
            condition: () =>
              isCreator &&
              res.data.is_forbidden &&
              res.data.warning_status === 1,
            deal: () => 'commit'
          },
          {
            condition: () =>
              isCreator &&
              res.data.is_forbidden &&
              res.data.warning_status === 0 &&
              res.data.allow_appeal,
            deal: () => 'forbid'
          },
          {
            condition: () =>
              isCreator && res.data.is_forbidden && !res.data.allow_appeal,
            deal: () => 'fail'
          }
        ]
        for (const { condition, deal, ...kwargs } of conditionHandlers) {
          const { breakReturn = false } = kwargs
          if (condition.call(this)) {
            status = deal.call(this, status)
            if (breakReturn) {
              return status
            }
          }
        }
        return status
      }
      useProps.value.status = checkForbidenStatus()
    } catch (e: any) {
      isForbidden.value = false
      const result = e.response?.data?.result
      const userInfo = await fetchUserPromise.catch(() => ({}))
      // 分享链接被禁止
      if (result === 'lightlinkForbid' && userInfo.is_company_account) {
        isForbidden.value = true
        useProps.value.status = 'shareForbid'
      } else if (!['lightlinkForbid'].includes(result)) {
        handleXhrError(e)
      }
    }
    flushUiState()
  }
  watch(isForbidden, flushUiState)
  if (autoFetch) {
    onMounted(() => {
      fetchStatus()
    })
  }
  return reactive({
    isForbidden,
    fetchStatus,
    ...toRefs(uiState),
    props: useProps,
    listeners: useListeners
  })
}
export const useRouterMobile = () => {
  const router = forbidPageRouterMobile
  const routeRef = computed(() => forbidPageRouterMobile?.app?.$route)
  const pageBack = () => {
    const route = unref(routeRef)
    if (route?.name === 'defaultPage' || !route?.name) {
      if (/(kdocs\.cn)|(docs\.wps\.cn)/.test(document.referrer)) {
        location.href = document.referrer
      } else {
        location.href = 'https://kdocs.cn/'
      }
      return
    }
    router.back()
  }
  const pageTitle = computed(() => {
    const route = unref(routeRef)
    return (
      {
        defaultPage: i18n.t('f1e2762445'),
        illegalAppeal: i18n.t('2fd888fd9a')
      }[route?.name || ''] || i18n.t('f1e2762445')
    )
  })
  onMounted(() => {
    forbidPageRouterMobile.push({
      name: 'defaultPage'
    })
  })
  const showRootRouteMobile = computed(
    () => forbidPageRouterMobile?.app?.$route?.name === 'defaultPage'
  )
  return reactive({
    router,
    route: routeRef,
    pageBack,
    pageTitle,
    showRootRouteMobile
  })
}
export const forbidPageSetup = (
  props: forbidPagePropsType,
  context: SetupContext
) => {
  const { emit } = context
  const {
    showRootRouteMobile,
    pageBack,
    pageTitle: topBarTitleMobile
  } = toRefs(useRouterMobile())
  const getState: () => forbidPageStateType = () => ({
    // 被分享者禁止访问
    shareForbid: {
      tipsImg: props.isMobile ? IMG_1_MOBILE : IMG_1,
      text1: i18n.t('800bee819a'),
      text2: '',
      text3: '',
      btn1: '',
      btn2: ''
    },
    // 禁止访问
    forbid: {
      tipsImg: props.isMobile ? IMG_1_MOBILE : IMG_1,
      text1: i18n.t('800bee819a'),
      text2: '',
      text3: '',
      btn1: i18n.t('bc599ee5cd'),
      btn2: i18n.t('8fe1ecec60')
    },
    // 提交成功
    commit: {
      tipsImg: IMG_2,
      text1: i18n.t('39a641bdc3'),
      text2: i18n.t('aed856a076'),
      text3: '',
      btn1: i18n.t('025e60d871'),
      btn2: i18n.t('8fe1ecec60')
    },
    // 解封失败
    fail: {
      tipsImg: IMG_1,
      text1: i18n.t('e385ada474'),
      text2: i18n.t('21805326f1'),
      text3: '',
      btn1: i18n.t('a20c3cecc5'),
      btn2: i18n.t('8fe1ecec60')
    }
  })
  const showAppeal = async () => {
    if (props.isMobile) {
      forbidPageRouterMobile.push({
        name: 'illegalAppeal'
      })
      return
    }
    const AppealDialog = await import('@/components/illegalAppeal/dialog')
    AppealDialog.default?.show?.(
      {
        afterAppeal: () => {
          AppealDialog?.default?.close?.()
          emit('statusChange', {
            status: 'commit'
          })
        },
        fileId: props.fileId
      },
      () => {},
      () => {}
    )
  }
  const backToKdocs = () => {
    window.location.href = 'https://kdocs.cn/latest'
  }
  const handleDownload = async () => {
    if (props.fileId && props.groupId) {
      try {
        await downloadFile({
          fileId: props.fileId,
          groupId: props.groupId
        })
      } catch (e) {
        emit('errorEmit', e)
      }
    }
  }
  const methods: forbidPageMethodType = {
    commit: {
      onBtn1Click: backToKdocs,
      onBtn2Click: handleDownload
    },
    fail: {
      onBtn1Click: showAppeal,
      onBtn2Click: handleDownload
    },
    forbid: {
      onBtn1Click: showAppeal,
      onBtn2Click: handleDownload
    },
    shareForbid: {
      onBtn1Click: backToKdocs
    }
  }
  const currentState = toRefs(reactive(getState()[props.status]))
  watch(
    () => props.status,
    (val) => {
      Object.keys(currentState).forEach((key) => {
        const newVal = getState()?.[val]?.[key]
        newVal !== undefined &&
          currentState?.[key]?.value !== undefined &&
          (currentState[key].value = newVal)
      })
    }
  )
  const currentMethods: methodType = methodsKeys.reduce((pre, cur) => {
    pre[cur] = (...args) => {
      methods?.[props.status]?.[cur]?.(...args)
    }
    return pre
  }, {} as methodType)
  const handleTopbarBackMobile = () => {
    pageBack.value?.()
  }
  function handleAppealMobile() {
    emit('statusChange', {
      status: 'commit'
    })
    pageBack.value?.()
    return
  }
  return {
    ...currentState,
    ...currentMethods,
    showRootRouteMobile,
    handleTopbarBackMobile,
    topBarTitleMobile,
    handleAppealMobile
  }
}
export default useForbidPage
